import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import StatCard from '../components/StatCard';
import ContractDocument from '../components/ContractDocument';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';

const COND_MAP = {
  no_damage:    { cls: 's-ac',  label: 'No Damage' },
  small_scratch:{ cls: 's-bk',  label: 'Small Scratch' },
  damage:       { cls: 's-ov',  label: 'Damage' },
  major_damage: { cls: 's-ov',  label: 'Major Damage' },
};

const PAY_LABELS = {
  stripe: '💳 Card (Stripe)',
  viva:   '🔵 Viva Wallet',
  paypal: '🅿️ PayPal',
  cash:   '💵 Cash',
  bank:   '🏦 Bank Transfer',
  online: '🌐 Pre-paid online',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function Row({ label, value, mono, style, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--bd)' }}>
      <span style={{ color: 'var(--tx3)', flexShrink: 0, marginRight: 10, fontSize: 10.5 }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', fontFamily: mono ? "'DM Mono',monospace" : undefined, ...style }}>
        {children || value || '—'}
      </span>
    </div>
  );
}

function Sect({ title }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', padding: '8px 0 3px', marginTop: 8, borderTop: '1px solid var(--bd)' }}>
      {title}
    </div>
  );
}

function InfoGrid({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', margin: '4px 0 2px' }}>
      {children}
    </div>
  );
}

function InfoCell({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 500, fontFamily: mono ? "'DM Mono',monospace" : undefined }}>{value}</div>
    </div>
  );
}

// ── CSV export (current filtered view) ────────────────────────────────────────
function exportHistoryCSV(rows, vehicles, invoices, range) {
  const condTxt = { no_damage: 'No Damage', small_scratch: 'Small Scratch', damage: 'Damage', major_damage: 'Major Damage' };
  const methodTxt = { stripe: 'Card (Stripe)', viva: 'Viva Wallet', paypal: 'PayPal', cash: 'Cash', bank: 'Bank Transfer', online: 'Pre-paid online' };
  const esc = (val) => {
    const s = (val === null || val === undefined) ? '' : String(val);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const headers = ['Contract','Type','Vehicle','Plate','Customer','Phone','Start','Pickup time','End','Return time','Days','Pickup location','Return location','Total (EUR)','Paid (EUR)','Outstanding (EUR)','Payment','Deposit (EUR)','Return condition','Status'];
  const lines = rows.map(r => {
    const v = vehicles.find(x => x.id === r.vid);
    const linked = invoices.filter(i => i.contract === r.id || i.contractId === r.id);
    const paid   = linked.filter(i => i.status === 'paid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
    const total  = parseFloat(r.agreedPrice) || 0;
    const deposit = parseFloat(r.depositCollected || r.deposit) || 0;
    return [
      r.id,
      r.type === 'lease' ? 'Lease' : 'Short-term',
      v?.name || '',
      v?.plate || '',
      r.customer || '',
      r.phone || '',
      r.startLabel || '',
      r.t1 || '',
      r.endLabel || '',
      r.t2 || '',
      r.calSpan || '',
      r.pickupLocName || '',
      r.returnLocName || '',
      total.toFixed(2),
      paid.toFixed(2),
      Math.max(0, total - paid).toFixed(2),
      methodTxt[r.payMethod] || r.payMethod || '',
      deposit ? deposit.toFixed(2) : '',
      condTxt[r.condRet] || 'Not recorded',
      r.status === 'cancelled' ? 'Cancelled' : 'Completed',
    ].map(esc).join(',');
  });
  // UTF-8 BOM so Excel renders the € / accented names correctly
  const csv = '﻿' + [headers.join(','), ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const stamp = range && (range.from || range.to)
    ? `${range.from || 'start'}_to_${range.to || 'end'}`
    : new Date().toISOString().slice(0, 10);
  a.download = `rental-history_${stamp}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── PDF report (print → Save as PDF) of the current filtered view ──────────────
function printHistoryPDF(rows, vehicles, invoices, cs, range) {
  const esc = (val) => String(val ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const eur = (n) => '€' + (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const condTxt = { no_damage: 'No Damage', small_scratch: 'Small Scratch', damage: 'Damage', major_damage: 'Major Damage' };

  let totTotal = 0, totPaid = 0, totOut = 0;
  const bodyRows = rows.map(r => {
    const v = vehicles.find(x => x.id === r.vid);
    const linked = invoices.filter(i => i.contract === r.id || i.contractId === r.id);
    const paid = linked.filter(i => i.status === 'paid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
    const total = parseFloat(r.agreedPrice) || 0;
    const out = Math.max(0, total - paid);
    totTotal += total; totPaid += paid; totOut += out;
    const dmg = r.condRet && r.condRet !== 'no_damage';
    return `<tr>
      <td>${esc(r.id)}</td>
      <td>${esc(v?.name || '')}${v?.plate ? `<br/><span class="sub">${esc(v.plate)}</span>` : ''}</td>
      <td>${esc(r.customer || '')}</td>
      <td>${esc(r.startLabel || '')}</td>
      <td>${esc(r.endLabel || '')}</td>
      <td class="num">${esc(r.calSpan || '')}</td>
      <td class="num">${eur(total)}</td>
      <td class="num">${eur(paid)}</td>
      <td class="num ${out > 0 ? 'due' : ''}">${eur(out)}</td>
      <td class="${dmg ? 'dmg' : ''}">${esc(condTxt[r.condRet] || 'Not recorded')}</td>
      <td>${r.status === 'cancelled' ? 'Cancelled' : 'Completed'}</td>
    </tr>`;
  }).join('');

  const periodTxt = range && (range.from || range.to)
    ? `Period: ${range.from || '…'} → ${range.to || '…'}`
    : 'Period: All records';
  const co = cs?.name || 'Your Company';
  const logoHtml = cs?.logoDataUrl
    ? `<img src="${cs.logoDataUrl}" style="max-height:60px;max-width:160px;object-fit:contain;" />`
    : `<div style="font-size:20px;font-weight:800;">${esc(co)}</div>`;
  const genDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Rental History Report</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color:#222; font-size:11px; margin:0; padding:22px; }
    .hd { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #333; padding-bottom:12px; margin-bottom:14px; }
    .hd .co { text-align:right; font-size:10px; color:#555; line-height:1.5; }
    h1 { font-size:18px; margin:8px 0 2px; }
    .meta { font-size:11px; color:#666; margin-bottom:12px; }
    .stats { display:flex; gap:14px; margin-bottom:14px; flex-wrap:wrap; }
    .stat { border:1px solid #ddd; border-radius:6px; padding:7px 14px; }
    .stat .lb { font-size:9px; text-transform:uppercase; letter-spacing:.05em; color:#888; }
    .stat .vl { font-size:15px; font-weight:700; }
    table { width:100%; border-collapse:collapse; }
    th { background:#333; color:#fff; font-size:9px; text-transform:uppercase; letter-spacing:.03em; padding:6px 8px; text-align:left; }
    th.num { text-align:right; }
    td { padding:5px 8px; border-bottom:1px solid #eee; vertical-align:top; }
    td.num { text-align:right; font-variant-numeric: tabular-nums; }
    td.due { color:#c0392b; font-weight:600; }
    td.dmg { color:#c0392b; }
    .sub { color:#999; font-size:9px; }
    tr:nth-child(even) td { background:#fafafa; }
    tfoot td { border-top:2px solid #333; font-weight:700; background:#fff !important; }
    .ft { margin-top:16px; font-size:9px; color:#999; text-align:center; }
  </style></head><body>
    <div class="hd">
      <div>${logoHtml}</div>
      <div class="co">
        ${cs?.vatNum ? `V.A.T ${esc(cs.vatNum)}<br/>` : ''}
        ${cs?.address ? `${esc(cs.address)}<br/>` : ''}
        ${cs?.phone ? `${esc(cs.phone)}<br/>` : ''}
        ${cs?.email ? esc(cs.email) : ''}
      </div>
    </div>
    <h1>Rental History Report</h1>
    <div class="meta">${esc(periodTxt)} · ${rows.length} record${rows.length === 1 ? '' : 's'} · Generated ${esc(genDate)}</div>
    <div class="stats">
      <div class="stat"><div class="lb">Rentals</div><div class="vl">${rows.length}</div></div>
      <div class="stat"><div class="lb">Total</div><div class="vl">${eur(totTotal)}</div></div>
      <div class="stat"><div class="lb">Paid</div><div class="vl">${eur(totPaid)}</div></div>
      <div class="stat"><div class="lb">Outstanding</div><div class="vl">${eur(totOut)}</div></div>
    </div>
    <table>
      <thead><tr>
        <th>Contract</th><th>Vehicle</th><th>Customer</th><th>Start</th><th>End</th>
        <th class="num">Days</th><th class="num">Total</th><th class="num">Paid</th><th class="num">Outstanding</th>
        <th>Return cond.</th><th>Status</th>
      </tr></thead>
      <tbody>${bodyRows || '<tr><td colspan="11" style="text-align:center;padding:20px;color:#999;">No records</td></tr>'}</tbody>
      <tfoot><tr>
        <td colspan="6">Totals — ${rows.length} record${rows.length === 1 ? '' : 's'}</td>
        <td class="num">${eur(totTotal)}</td>
        <td class="num">${eur(totPaid)}</td>
        <td class="num">${eur(totOut)}</td>
        <td colspan="2"></td>
      </tr></tfoot>
    </table>
    <div class="ft">${esc(co)} — Rental History Report — use your browser's “Save as PDF” in the print dialog</div>
    <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
  </body></html>`);
  w.document.close();
  w.focus();
  return w;
}

// ── History Detail Modal ──────────────────────────────────────────────────────
function HistoryDetailModal({ record, vehicle, invoice, invoiceList, onClose, onViewInvoice }) {
  const { companySettings, getUserName, openModal, resolveAgreementTemplate } = useApp();
  const [showAgreement, setShowAgreement] = useState(false);

  const condRet = COND_MAP[record.condRet] || { cls: 's-av', label: 'Not recorded' };
  const condPre = COND_MAP[record.condPre] || null;

  const isCancelled = record.status === 'cancelled';
  const isLease = record.type === 'lease';

  // Live-fallback template resolution for old records signed before agreementSaved snapshot existed
  const liveTmpl = (!record.agreementSaved && resolveAgreementTemplate)
    ? resolveAgreementTemplate(record.vid, isLease ? 'lease' : 'rental')
    : null;
  const tmplBody = record.agreementSaved?.body || liveTmpl?.body || '';
  const tmplClauses = record.agreementSaved?.clauses || liveTmpl?.clauses || [];
  const tmplName = record.agreementSaved?.templateName
    || liveTmpl?.name
    || (isLease ? 'Long-term Lease Agreement' : 'Rental Agreement');

  // Synthetic snapshot built on-the-fly for old records that were signed but lack agreementSaved.
  // Lets the "📋 Rental Agreement" View/Print section render for legacy rentals.
  const sigDataAny = record.signData || record.presignData;
  const wasSigned = !!(sigDataAny || record.signed || record.presigned);
  const effectiveAg = record.agreementSaved || (wasSigned ? {
    templateId: liveTmpl?.id || null,
    templateName: tmplName,
    body: tmplBody,
    clauses: tmplClauses,
    customerName: record.customer || '',
    passport: record.passport || '',
    licence: record.licence || '',
    vehicleName: vehicle?.name || '',
    vehiclePlate: vehicle?.plate || '',
    startLabel: record.startLabel || '',
    endLabel: record.endLabel || '',
    t1: record.t1 || '',
    t2: record.t2 || '',
    days: record.calSpan || record.days || 1,
    rate: record.rate || 0,
    insuranceName: record.insurance || '',
    extras: record.extras || [],
    agreedPrice: record.agreedPrice || 0,
    signData: sigDataAny,
    signedAt: record.signedAt || record.presignedAt || record.checkedInAt || null,
    signedAtFormatted: record.presignedAt
      || record.checkInTime
      || (record.signedAt ? new Date(record.signedAt).toLocaleString('en-GB') : ''),
  } : null);

  // Build the shared context (saved or live)
  const ctx = record.agreementSaved?.context || buildContractContext({
    rental: record,
    vehicle,
    company: companySettings,
    signatureDataUrl: record.signData || record.presignData,
    signedAt: record.signedAt || record.presignedAt,
  });
  const tmpl = (record.agreementSaved?.body || tmplBody)
    ? { name: tmplName, body: record.agreementSaved?.body || tmplBody, clauses: tmplClauses }
    : liveTmpl;
  const renderedHtml = record.agreementSaved?.renderedHtml;

  function printAgreement() {
    printContract(tmpl || { body: renderedHtml }, ctx, { title: `Contract ${record.id}` });
  }

  return (
    <div
      className="ovl open"
      style={{ zIndex: 300 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal" style={{ width: 660 }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="mhd">
          <div className="m-ico" style={{ background: isCancelled ? 'var(--sur2)' : 'var(--gr-l)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={isCancelled ? 'var(--tx3)' : 'var(--gr)'} strokeWidth="2">
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>
          <div>
            <div className="m-tit">{vehicle?.icon} {vehicle?.name?.split('–')[0]?.trim() || record.vid}</div>
            <div className="m-sub">
              {record.id} · {vehicle?.plate}
              {vehicle?.year ? ` · ${vehicle.year}` : ''}
            </div>
          </div>
          <button className="m-cls" onClick={onClose}>×</button>
        </div>

        {/* ── Body ── */}
        <div className="mbdy" style={{ maxHeight: 600, overflowY: 'auto', padding: '14px 18px' }}>

          {/* ── Status bar ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {isCancelled
              ? <span className="sp s-ov">✕ Cancelled</span>
              : <span className="sp s-ac">✓ Completed</span>
            }
            {record.checkInTime && <span style={{ fontSize: 10, color: 'var(--tx3)' }}>· Check-in {record.checkInTime}</span>}
            {record.returnedAt  && <span style={{ fontSize: 10, color: 'var(--tx3)' }}>· Returned {new Date(record.returnedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
            {record.returnKm    && <span style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>· {record.returnKm.toLocaleString()} km</span>}
          </div>

          {/* ── Customer card ── */}
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--tx)' }}>{record.customer}</div>
            <InfoGrid>
              <InfoCell label="Phone"          value={record.phone} />
              <InfoCell label="Email"          value={record.email} />
              <InfoCell label="Passport / ID"  value={record.passport}    mono />
              <InfoCell label="Driving Licence" value={record.licence}    mono />
              <InfoCell label="Licence Expiry" value={record.licExpiry}   mono />
              <InfoCell label="Nationality"    value={record.nationality} />
              <InfoCell label="Date of Birth"  value={record.dob}         mono />
            </InfoGrid>
          </div>

          {/* ── Rental period card ── */}
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
              <div>
                <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Pickup</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{record.startLabel || '—'}</div>
                {record.t1 && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>⏱ {record.t1}</div>}
                {record.pickupLocName && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>📍 {record.pickupLocName}</div>}
              </div>
              <div>
                <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Return</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{record.endLabel || '—'}</div>
                {record.t2 && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>⏱ {record.t2}</div>}
                {record.returnLocName && record.returnLocName !== record.pickupLocName && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>📍 {record.returnLocName}</div>}
              </div>
              {record.calSpan && (
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Duration</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{record.calSpan} day{record.calSpan > 1 ? 's' : ''}</div>
                </div>
              )}
              {record.rate && (
                <div>
                  <div style={{ fontSize: 9, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Daily Rate</div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>€{record.rate}/day</div>
                </div>
              )}
            </div>
          </div>

          {/* ── Lease Terms (only for leases) ── */}
          {isLease && (
            <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--bl)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                📅 Lease Terms
              </div>
              <InfoGrid>
                <InfoCell label="Term"             value={record.termMonths ? `${record.termMonths} months` : null} />
                <InfoCell label="Monthly Rate"     value={record.monthlyRate ? `€${record.monthlyRate}/mo` : null} mono />
                <InfoCell label="Annual Mileage"   value={record.annualMileageCap ? `${parseInt(record.annualMileageCap).toLocaleString()} km/yr` : 'Unlimited'} mono />
                <InfoCell label="Billing Cadence"  value={record.billingCadence} />
                <InfoCell label="VAT Treatment"    value={record.vatMode === 'inclusive' ? 'Inclusive' : record.vatMode === 'exclusive' ? `+ VAT @ ${record.vatRate || 19}%` : null} />
                <InfoCell label="Deposit Method"   value={record.depositMethod} />
                <InfoCell label="Included"         value={record.includedServices} />
                <InfoCell label="Months Invoiced"  value={Array.isArray(record.invoicedMonths) ? `${record.invoicedMonths.length} of ${record.termMonths || '?'}` : null} mono />
              </InfoGrid>
              {record.renewedFrom && (
                <div style={{ marginTop: 6, fontSize: 10, color: 'var(--tx3)' }}>
                  🔄 Renewed from <code style={{ fontFamily: "'DM Mono',monospace" }}>{record.renewedFrom}</code>
                </div>
              )}
            </div>
          )}

          {/* ── Insurance & Extras ── */}
          {(record.insurance || record.extras?.length > 0) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {record.insurance && (
                <span style={{ fontSize: 11, background: 'var(--bl-l)', border: '1px solid var(--bl-b)', color: 'var(--bl)', borderRadius: 20, padding: '2px 10px', fontWeight: 500 }}>
                  🛡️ {record.insurance}
                </span>
              )}
              {record.extras?.map((ex, i) => (
                <span key={i} style={{ fontSize: 11, background: 'var(--am-l)', border: '1px solid var(--am-b)', color: 'var(--am)', borderRadius: 20, padding: '2px 10px', fontWeight: 500 }}>
                  ➕ {ex}
                </span>
              ))}
            </div>
          )}

          {/* ── Financial card ── */}
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
            {/* Contract pill */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: 'var(--tx3)' }}>Contract</span>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 5, padding: '1px 8px' }}>{record.id}</span>
            </div>
            {/* Total */}
            <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 7, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--tx2)' }}>Agreed Total</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--gr)' }}>€{record.agreedPrice}</span>
            </div>
            {/* Payment method — show original + desk separately if different */}
            <Row label="Payment (booking)" value={PAY_LABELS[record.payMethod] || record.payMethod || '—'} />
            {record.deskPayMethod && record.deskPayMethod !== record.payMethod && (
              <Row label="Payment (at desk)" value={PAY_LABELS[record.deskPayMethod] || record.deskPayMethod} />
            )}
            {/* Deposit — from insurance plan, live at check-in */}
            {(record.depositCollected > 0 || record.deposit > 0) ? (
              <Row label="Deposit collected" value={`€${record.depositCollected || record.deposit}${record.depositPayMethod ? ' · ' + (PAY_LABELS[record.depositPayMethod] || record.depositPayMethod) : ''}`} mono />
            ) : (
              <Row label="Deposit" value="No deposit required" style={{ color: 'var(--gr)' }} />
            )}
            {record.depositRefund && (
              <Row label="Deposit returned"
                value={
                  record.depositRefund === 'full'    ? `✅ Full — €${record.depositReturned || record.depositCollected || record.deposit || 0}` :
                  record.depositRefund === 'partial'  ? `🔸 Partial — €${record.depositReturned || 0} returned · €${record.depositDeducted || 0} deducted` :
                                                       `❌ No refund — €${record.depositDeducted || record.depositCollected || record.deposit || 0} deducted`
                }
                style={{ color: record.depositRefund === 'full' ? 'var(--gr)' : record.depositRefund === 'partial' ? 'var(--am)' : 'var(--ac)' }}
              />
            )}
            {record.extraFee > 0 && (
              <Row label="Extra charge" value={`€${record.extraFee}${record.extraNote ? ' · ' + record.extraNote : ''}`} style={{ color: 'var(--ac)' }} />
            )}
            {record.bankTransferVerified && (
              <div style={{ marginTop: 6, background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 5, padding: '5px 8px', fontSize: 10, color: 'var(--gr)', fontWeight: 600 }}>
                ✅ Bank transfer payment confirmed
              </div>
            )}
          </div>

          {/* ── Vehicle Condition ── */}
          <Sect title="Vehicle Condition" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
            {condPre && <span style={{ fontSize: 10, color: 'var(--tx3)' }}>Pre-rental: <strong>{condPre.label}</strong></span>}
            {condPre && <span style={{ color: 'var(--bd)' }}>→</span>}
            <span className={`sp ${condRet.cls}`}>{condRet.label}</span>
          </div>
          {record.condRetNote && (
            <div style={{ marginTop: 4, padding: '6px 10px', background: (record.condRet === 'damage' || record.condRet === 'major_damage') ? 'var(--ac-l)' : 'var(--sur2)', border: `1px solid ${(record.condRet === 'damage' || record.condRet === 'major_damage') ? 'var(--ac-b)' : 'var(--bd)'}`, borderRadius: 6, fontSize: 11, color: 'var(--tx2)' }}>
              ⚠️ {record.condRetNote}
            </div>
          )}

          {/* ── Cancellation Summary (only for cancelled records) ── */}
          {isCancelled && record.cancellationSnapshot && (() => {
            const cs = record.cancellationSnapshot;
            const headerColor = cs.finalRefund > 0 ? 'var(--gr)' : 'var(--ac)';
            const headerBg    = cs.finalRefund > 0 ? 'var(--gr-l)' : 'var(--ac-l)';
            const headerBd    = cs.finalRefund > 0 ? 'var(--gr-b)' : 'var(--ac-b)';
            return (
              <>
                <Sect title="❌ Cancellation Summary" />
                <div style={{ background: headerBg, border: `1px solid ${headerBd}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${headerBd}` }}>
                    <div style={{ fontSize: 10, color: headerColor, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>
                      {cs.finalRefund > 0 ? 'Refund processed' : 'No refund (per policy)'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: headerColor, fontFamily: "'DM Mono',monospace" }}>
                      €{(cs.finalRefund || 0).toFixed(2)}
                    </div>
                  </div>
                  <InfoGrid>
                    <InfoCell label="Cancelled by"       value="Customer (self-service)" />
                    <InfoCell label="Cancelled at"       value={cs.cancelledAt ? new Date(cs.cancelledAt).toLocaleString('en-GB') : null} />
                    <InfoCell label="Scenario"           value={cs.scenario === 'full' ? 'Full (within free window)' : cs.scenario === 'late' ? 'Late cancellation' : cs.scenario === 'no_show' ? 'No-show' : cs.scenario === 'partial' ? 'Partial refund' : cs.scenario} />
                    <InfoCell label="Policy refund %"    value={cs.refundPercent != null ? `${cs.refundPercent}%` : null} />
                  </InfoGrid>

                  <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                      Refund Calculation
                    </div>
                    <Row label="Originally paid"           value={`€${(cs.originalPaid || 0).toFixed(2)}`} mono />
                    {cs.differenceOwedAtCancel > 0 && (
                      <Row label="(Difference unpaid at desk)" value={`−€${cs.differenceOwedAtCancel.toFixed(2)}`} mono style={{ color: 'var(--tx3)' }} />
                    )}
                    <Row label={`Policy refund (${cs.refundPercent || 0}%)`} value={`€${(cs.refundEligible || 0).toFixed(2)}`} mono />
                    {cs.deductFee && cs.transactionFee > 0 && (
                      <Row label="Transaction fee deducted" value={`−€${cs.transactionFee.toFixed(2)}`} mono style={{ color: 'var(--ac)' }} />
                    )}
                    <Row label="Final refund" value={`€${(cs.finalRefund || 0).toFixed(2)}`} mono style={{ fontWeight: 700, color: headerColor }} />
                  </div>
                </div>
              </>
            );
          })()}

          {/* ── Lease Closeout / Settlement (only for closed leases) ── */}
          {isLease && record.leaseCloseout && (() => {
            const co = record.leaseCloseout;
            const headerColor = co.finalDue > 0 ? 'var(--am)' : co.depositReturned > 0 ? 'var(--gr)' : 'var(--tx2)';
            const headerBg    = co.finalDue > 0 ? 'var(--am-l)' : co.depositReturned > 0 ? 'var(--gr-l)' : 'var(--sur2)';
            const headerBd    = co.finalDue > 0 ? 'var(--am-b)' : co.depositReturned > 0 ? 'var(--gr-b)' : 'var(--bd)';
            return (
              <>
                <Sect title="📊 Lease Settlement Summary" />
                <div style={{ background: headerBg, border: `1px solid ${headerBd}`, borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                  {/* Headline outcome */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${headerBd}` }}>
                    <div style={{ fontSize: 10, color: headerColor, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>
                      {co.finalDue > 0 ? 'Customer was billed' : co.depositReturned > 0 ? 'Refund processed' : 'Settled in full'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: headerColor, fontFamily: "'DM Mono',monospace" }}>
                      {co.finalDue > 0 ? `€${co.finalDue.toFixed(2)}` : co.depositReturned > 0 ? `€${co.depositReturned.toFixed(2)}` : '€0.00'}
                    </div>
                  </div>

                  {/* Mileage breakdown */}
                  <InfoGrid>
                    <InfoCell label="Start KM"          value={co.startKm != null ? co.startKm.toLocaleString() : null}   mono />
                    <InfoCell label="Return KM"         value={co.returnKm != null ? co.returnKm.toLocaleString() : null} mono />
                    <InfoCell label="Driven"            value={co.kmDriven != null ? `${co.kmDriven.toLocaleString()} km` : null} mono />
                    <InfoCell label="Months Elapsed"    value={co.monthsElapsed != null ? `${co.monthsElapsed.toFixed(1)} mo` : null} mono />
                    {co.annualMileageCap > 0 && (
                      <>
                        <InfoCell label="Annual Cap"    value={`${co.annualMileageCap.toLocaleString()} km/yr`} mono />
                        <InfoCell label="Pro-rated Allowed" value={co.allowedKm != null ? `${co.allowedKm.toLocaleString()} km` : null} mono />
                      </>
                    )}
                  </InfoGrid>

                  {/* Charges breakdown */}
                  <div style={{ marginTop: 10, padding: '8px 10px', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                      Charges
                    </div>
                    {co.excessKmCharge > 0 && (
                      <Row label={`Excess mileage (${co.excessKm} km × €${(co.excessKmRate || 0).toFixed(2)}/km)`} value={`€${co.excessKmCharge.toFixed(2)}`} mono />
                    )}
                    {co.damageFee > 0 && (
                      <Row label={`Damage${co.damageNote ? ' — ' + co.damageNote : ''}`} value={`€${co.damageFee.toFixed(2)}`} mono style={{ color: 'var(--ac)' }} />
                    )}
                    {co.lateFeeAmt > 0 && (
                      <Row label={`Late fee (${co.daysLate} day${co.daysLate === 1 ? '' : 's'} × €${co.lateFeePerDay}/day)`} value={`€${co.lateFeeAmt.toFixed(2)}`} mono style={{ color: 'var(--am)' }} />
                    )}
                    {co.buyout && co.buyoutPrice > 0 && (
                      <Row label="🚗 Vehicle buy-out" value={`€${co.buyoutPrice.toFixed(2)}`} mono style={{ color: 'var(--bl)' }} />
                    )}
                    {co.totalCharges === 0 && (
                      <div style={{ fontSize: 11, color: 'var(--gr)', textAlign: 'center', padding: '6px 0' }}>
                        ✓ No charges — clean return
                      </div>
                    )}
                    {co.totalCharges > 0 && (
                      <Row label="Total charges" value={`€${co.totalCharges.toFixed(2)}`} mono style={{ fontWeight: 700, color: 'var(--tx)' }} />
                    )}
                  </div>

                  {/* Deposit reconciliation */}
                  {co.deposit > 0 && (
                    <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                        Deposit
                      </div>
                      <Row label="Held" value={`€${co.deposit.toFixed(2)}`} mono />
                      {co.depositDeducted > 0 && <Row label="Applied to charges" value={`−€${co.depositDeducted.toFixed(2)}`} mono style={{ color: 'var(--ac)' }} />}
                      {co.depositReturned > 0 && <Row label="Returned to customer" value={`€${co.depositReturned.toFixed(2)}`} mono style={{ color: 'var(--gr)' }} />}
                    </div>
                  )}

                  {/* Closed-by audit */}
                  <div style={{ marginTop: 8, fontSize: 10, color: 'var(--tx3)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>🏁 Closed{co.closedBy ? ` by ${getUserName(co.closedBy)}` : ''}</span>
                    <span>{co.closedAt ? new Date(co.closedAt).toLocaleString('en-GB') : ''}</span>
                  </div>

                  {/* Closeout signature */}
                  {co.closeoutSignature && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 3 }}>✍️ Customer sign-off on closeout</div>
                      <img src={co.closeoutSignature} alt="Sign-off"
                           style={{ maxHeight: 50, background: '#fff', border: '1px solid var(--bd)', borderRadius: 4 }} />
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {/* ── Staff Assignment / Audit Trail ── */}
          {(record.checkedInBy || record.checkedOutBy || record.cashCollectedBy) && (
            <>
              <Sect title="Staff Activity" />
              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                {record.checkedInBy && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '3px 0', borderBottom: '1px solid var(--bd)' }}>
                    <span style={{ color: 'var(--tx2)' }}>✅ Checked in by <strong>{getUserName(record.checkedInBy)}</strong></span>
                    <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{record.checkedInAt || record.checkInTime || ''}</span>
                  </div>
                )}
                {record.checkedOutBy && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '3px 0', borderBottom: record.cashCollectedBy ? '1px solid var(--bd)' : 'none' }}>
                    <span style={{ color: 'var(--tx2)' }}>🏁 Checked out by <strong>{getUserName(record.checkedOutBy)}</strong></span>
                    <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{record.checkedOutAt || ''}</span>
                  </div>
                )}
                {record.cashCollectedBy && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '3px 0' }}>
                    <span style={{ color: 'var(--tx2)' }}>💵 Cash collected by <strong>{getUserName(record.cashCollectedBy)}</strong></span>
                    <span style={{ fontSize: 10, color: 'var(--gr)', fontWeight: 600 }}>€{record.cashAmount || 0}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Document Photos ── */}
          {(record.passportPhoto || record.idFile || record.licencePhoto || record.licenceFile) && (
            <>
              <Sect title="📎 Documents" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '4px 0' }}>
                {(record.passportPhoto || record.idFile) && (
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 3 }}>Passport / ID</div>
                    <img src={record.passportPhoto || (record.idFile instanceof File ? URL.createObjectURL(record.idFile) : record.idFile)} alt="ID"
                      style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', height: 90, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => window.open(record.passportPhoto || record.idFile, '_blank')} />
                  </div>
                )}
                {(record.licencePhoto || record.licenceFile) && (
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 3 }}>Driving Licence</div>
                    <img src={record.licencePhoto || (record.licenceFile instanceof File ? URL.createObjectURL(record.licenceFile) : record.licenceFile)} alt="Licence"
                      style={{ width: '100%', borderRadius: 6, border: '1px solid var(--bd)', height: 90, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => window.open(record.licencePhoto || record.licenceFile, '_blank')} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Check-in Photos ── */}
          {record.checkInPhotos?.length > 0 && (
            <>
              <Sect title="📷 Check-in Photos" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5, margin: '4px 0' }}>
                {record.checkInPhotos.map((src, i) => (
                  <img key={i} src={src} alt={`CI ${i + 1}`}
                    style={{ width: '100%', borderRadius: 5, border: '1px solid var(--bd)', height: 70, objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => window.open(src, '_blank')} />
                ))}
              </div>
            </>
          )}

          {/* ── Return Photos ── */}
          {record.returnPhotos?.length > 0 && (
            <>
              <Sect title="📷 Return Photos" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5, margin: '4px 0' }}>
                {record.returnPhotos.map((src, i) => (
                  <img key={i} src={src} alt={`Ret ${i + 1}`}
                    style={{ width: '100%', borderRadius: 5, border: '1px solid var(--bd)', height: 70, objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => window.open(src, '_blank')} />
                ))}
              </div>
            </>
          )}

          {/* ── Signature ── */}
          {(record.signed || record.presigned || record.presignData || record.signData) && (
            <>
              <Sect title="✍️ Signature" />
              {record.presigned && record.presignData ? (
                <>
                  <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 5, padding: '4px 8px', marginBottom: 4, fontSize: 10, color: 'var(--gr)', fontWeight: 600 }}>
                    ✅ Pre-signed online{record.presignedAt ? ` — ${record.presignedAt}` : ''}
                  </div>
                  <img src={record.presignData} alt="Signature"
                    style={{ width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 5, maxHeight: 60, objectFit: 'contain' }} />
                </>
              ) : record.signData ? (
                <>
                  <div style={{ fontSize: 10, color: 'var(--gr)', marginBottom: 3 }}>✓ Signed at check-in{record.checkInTime ? ` — ${record.checkInTime}` : ''}</div>
                  <img src={record.signData} alt="Signature"
                    style={{ width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 5, maxHeight: 60, objectFit: 'contain' }} />
                </>
              ) : (
                <div style={{ fontSize: 10, color: 'var(--gr)' }}>✓ Signed at check-in{record.checkInTime ? ` — ${record.checkInTime}` : ''}</div>
              )}
            </>
          )}

          {/* ── Rental Agreement ── */}
          {effectiveAg && (
            <>
              <Sect title="📋 Rental Agreement" />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0' }}>
                <div style={{ fontSize: 10, color: 'var(--tx2)' }}>
                  ✍️ {effectiveAg.customerName}{effectiveAg.signedAtFormatted ? ` · ${effectiveAg.signedAtFormatted}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAgreement(true)}>📋 View</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => printAgreement()}>🖨️ Print</button>
                </div>
              </div>
            </>
          )}

          {/* ── Invoices —
                • 1 invoice → simple inline row
                • >1 invoices → summary card + "View All Invoices" button ── */}
          {(invoiceList && invoiceList.length > 0) && (() => {
            const totalCount = invoiceList.length;

            // Single-invoice case → inline clickable row
            if (totalCount === 1) {
              const inv = invoiceList[0];
              return (
                <>
                  <Sect title="📄 Pro Forma" />
                  <button
                    type="button"
                    onClick={() => onViewInvoice(inv.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 7,
                      padding: '10px 14px', cursor: 'pointer', textAlign: 'left',
                      transition: 'all .12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--ac-l)'; e.currentTarget.style.borderColor = 'var(--ac-b)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--sur2)'; e.currentTarget.style.borderColor = 'var(--bd)'; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{inv.num}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                        €{parseFloat(inv.total || 0).toFixed(2)} ·{' '}
                        <span style={{ fontWeight: 600, color: inv.status === 'paid' ? 'var(--gr)' : inv.status === 'overdue' ? 'var(--ac)' : 'var(--am)' }}>
                          {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                        </span>
                        {inv.issued ? ` · ${inv.issued}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 14, color: 'var(--tx3)', marginLeft: 10 }}>›</span>
                  </button>
                </>
              );
            }

            // Multi-invoice case → summary + "View All Invoices" button
            const paidList = invoiceList.filter(i => i.status === 'paid');
            const paidCount  = paidList.length;
            const unpaidCount = totalCount - paidCount;
            const paidAmount = paidList.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
            const pendingTotal = invoiceList
              .filter(i => i.status !== 'paid')
              .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

            return (
              <>
                <Sect title={`📄 ${isLease ? 'Lease Pro Formas' : 'Pro Formas'} · ${totalCount}`} />
                <div style={{
                  display: 'flex', gap: 12, padding: '10px 12px',
                  background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 8,
                  fontSize: 12, marginBottom: 8,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Paid</div>
                    <div style={{ fontWeight: 700, color: 'var(--gr)', fontSize: 15 }}>€{paidAmount.toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>{paidCount} of {totalCount}</div>
                  </div>
                  <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Unpaid</div>
                    <div style={{ fontWeight: 700, color: unpaidCount > 0 ? 'var(--am)' : 'var(--gr)', fontSize: 15 }}>{unpaidCount}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>pro forma{unpaidCount === 1 ? '' : 's'}</div>
                  </div>
                  <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 12 }}>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Outstanding</div>
                    <div style={{ fontWeight: 700, color: pendingTotal > 0 ? 'var(--am)' : 'var(--gr)', fontSize: 15 }}>€{pendingTotal.toFixed(2)}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { onClose(); openModal('lease-invoices', record.id); }}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  📊 View All Pro Formas
                </button>
              </>
            );
          })()}

          {/* ── Notes ── */}
          {record.notes && (
            <>
              <Sect title="Notes" />
              <div style={{ padding: '6px 10px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 6, fontSize: 11, color: 'var(--tx2)' }}>
                💬 {record.notes}
              </div>
            </>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="mft">
          <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>
        </div>

        {/* ── Agreement Viewer Overlay — uses shared ContractDocument ── */}
        {showAgreement && effectiveAg && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowAgreement(false)}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>📋 {tmplName} · {record.id}</div>
                <button onClick={() => setShowAgreement(false)} style={{ fontSize: 22, cursor: 'pointer', border: 'none', background: 'none', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: 'var(--sur2)' }}>
                <ContractDocument template={tmpl} context={ctx} prerenderedHtml={renderedHtml} paperLook={true} />
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowAgreement(false)}>Close</button>
                <button className="btn btn-primary" onClick={() => printAgreement()}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function RentalHistoryPage() {
  const { rentalHistory, vehicles, invoices, companySettings, openModal, showToast } = useApp();
  const [search, setSearch]         = useState('');
  const [filterDmg, setFilterDmg]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVeh, setFilterVeh]   = useState('');
  const [filterType, setFilterType] = useState('');   // '' = all, 'rental' = short-term, 'lease' = lease
  const [filterFrom, setFilterFrom] = useState('');   // YYYY-MM-DD — start of date window
  const [filterTo, setFilterTo]     = useState('');   // YYYY-MM-DD — end of date window
  const [selected, setSelected]     = useState(null);

  const getVehicle = (vid) => vehicles.find(v => v.id === vid);
  const getInvoice  = (rid) => invoices.find(inv => inv.contract === rid || inv.contractId === rid);
  const getInvoices = (rid) => invoices.filter(inv => inv.contract === rid || inv.contractId === rid);

  const q = search.toLowerCase();
  const records = useMemo(() => {
   const fromD = filterFrom ? new Date(filterFrom + 'T00:00:00') : null;
   const toD   = filterTo   ? new Date(filterTo   + 'T23:59:59') : null;
   return rentalHistory.filter(r => {
    const v = getVehicle(r.vid);
    const matchQ = !q || r.customer?.toLowerCase().includes(q)
      || (v?.name || '').toLowerCase().includes(q)
      || r.id?.toLowerCase().includes(q);
    const matchDmg = !filterDmg
      || (filterDmg === 'any_damage'
          ? (r.condRet && r.condRet !== 'no_damage')
          : r.condRet === filterDmg);
    const matchSt  = !filterStatus || r.status === filterStatus;
    const matchVeh = !filterVeh || r.vid === filterVeh;
    // Type filter — leases have r.type === 'lease', short-term rentals have undefined or 'rental'
    const recType = r.type === 'lease' ? 'lease' : 'rental';
    const matchType = !filterType || recType === filterType;
    // Date-window filter — keep records whose rental period OVERLAPS [from, to].
    // Prefer ISO start/end; fall back to parsing the display labels ("22 Mar 2026").
    let matchDate = true;
    if (fromD || toD) {
      const rs = r.start ? new Date(r.start) : (r.startLabel ? new Date(r.startLabel) : null);
      const re = r.end   ? new Date(r.end)   : (r.endLabel   ? new Date(r.endLabel)   : rs);
      const startOk = rs && !isNaN(rs);
      const endOk   = re && !isNaN(re);
      if (!startOk && !endOk) matchDate = false;
      else {
        const lo = startOk ? rs : re;
        const hi = endOk   ? re : rs;
        matchDate = (!fromD || hi >= fromD) && (!toD || lo <= toD);
      }
    }
    return matchQ && matchDmg && matchSt && matchVeh && matchType && matchDate;
   });
  }, [rentalHistory, q, filterDmg, filterStatus, filterVeh, filterType, filterFrom, filterTo]);

  // Type counts for the pill labels (always totals, independent of the type pill)
  const leaseCount  = rentalHistory.filter(r => r.type === 'lease').length;
  const rentalCount = rentalHistory.length - leaseCount;

  // Any active filter? (drives whether the summary cards reflect a subset)
  const anyFilter = !!(filterDmg || filterStatus || filterVeh || filterType || search || filterFrom || filterTo);

  // Summary stats reflect the CURRENT filtered view, so a From→To window
  // gives a true "rentals / revenue in this period" snapshot.
  const totalRevenue = records.reduce((s, r) => s + (r.agreedPrice || 0), 0);
  const damageCount  = records.filter(r => r.condRet && r.condRet !== 'no_damage').length;
  const avgDays      = records.length
    ? Math.round(records.reduce((s, r) => s + (r.calSpan || 0), 0) / records.length)
    : 0;

  return (
    <div>
      {/* Stats — clickable as filters */}
      <div className="sg sg-4" style={{ marginBottom: 14 }}>
        <StatCard
          label="Total Rentals"
          value={anyFilter ? `${records.length} / ${rentalHistory.length}` : rentalHistory.length}
          desc={anyFilter ? 'Filtered — click to clear all' : 'All records'}
          active={anyFilter}
          onClick={() => {
            setFilterDmg(''); setFilterStatus(''); setFilterVeh(''); setFilterType(''); setSearch('');
            setFilterFrom(''); setFilterTo('');
          }}
        />
        <StatCard
          label="Total Revenue"
          value={`€${totalRevenue.toLocaleString()}`}
          desc={anyFilter ? 'In current filter' : 'From completed rentals'}
          variant="gr"
        />
        <StatCard
          label="Damage Returns"
          value={damageCount}
          desc={filterDmg && filterDmg !== 'no_damage' ? 'Filtering — click to clear' : 'Click to filter damaged'}
          variant="ac"
          active={filterDmg && filterDmg !== 'no_damage'}
          onClick={() => {
            // Toggle: if already filtering damages, clear; else show ALL damaged returns
            if (filterDmg && filterDmg !== 'no_damage') setFilterDmg('');
            else setFilterDmg('any_damage');
          }}
        />
        <StatCard label="Avg Duration" value={`${avgDays} days`} desc="Per rental" />
      </div>

      {/* Type pills — All / Short-term / Lease */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { val: '',       label: 'All',          count: rentalHistory.length, icon: '📂' },
          { val: 'rental', label: 'Short-term',   count: rentalCount,          icon: '🔑' },
          { val: 'lease',  label: 'Leases',       count: leaseCount,           icon: '📅' },
        ].map(p => {
          const active = filterType === p.val;
          return (
            <button
              key={p.val}
              type="button"
              onClick={() => setFilterType(p.val)}
              style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${active ? 'var(--ac)' : 'var(--bd)'}`,
                borderRadius: 20,
                background: active ? 'var(--ac-l)' : 'var(--sur)',
                color: active ? 'var(--ac)' : 'var(--tx2)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{p.icon} {p.label}</span>
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: active ? 'var(--ac)' : 'var(--sur2)',
                color: active ? '#fff' : 'var(--tx3)',
                borderRadius: 10, padding: '1px 7px',
                fontFamily: "'DM Mono',monospace",
              }}>{p.count}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="tb2">
        <div className="srch">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Search customer, vehicle, contract..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="fsel" value={filterDmg} onChange={e => setFilterDmg(e.target.value)}>
          <option value="">All conditions</option>
          <option value="any_damage">⚠️ Any damage</option>
          <option value="no_damage">✓ No damage</option>
          <option value="small_scratch">Small scratch</option>
          <option value="damage">Damage</option>
          <option value="major_damage">Major damage</option>
        </select>
        <select className="fsel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="complete">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="fsel" value={filterVeh} onChange={e => setFilterVeh(e.target.value)}>
          <option value="">All vehicles</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        {/* Date window — From / To (matches rentals whose period overlaps the range) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 10.5, color: 'var(--tx3)', fontWeight: 600 }}>From</span>
          <input type="date" className="fsel" value={filterFrom} max={filterTo || undefined}
            onChange={e => setFilterFrom(e.target.value)} style={{ padding: '0 6px' }} />
          <span style={{ fontSize: 10.5, color: 'var(--tx3)', fontWeight: 600 }}>To</span>
          <input type="date" className="fsel" value={filterTo} min={filterFrom || undefined}
            onChange={e => setFilterTo(e.target.value)} style={{ padding: '0 6px' }} />
          {(filterFrom || filterTo) && (
            <button type="button" title="Clear dates"
              onClick={() => { setFilterFrom(''); setFilterTo(''); }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--tx3)', fontSize: 14, lineHeight: 1, padding: '0 2px' }}>×</button>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" disabled={records.length === 0}
            onClick={() => {
              if (records.length === 0) { showToast('No records to export'); return; }
              exportHistoryCSV(records, vehicles, invoices, { from: filterFrom, to: filterTo });
              showToast(`⬇ Exported ${records.length} record${records.length === 1 ? '' : 's'} to CSV`);
            }}>⬇ CSV{anyFilter ? ` (${records.length})` : ''}</button>
          <button className="btn btn-ghost btn-sm" disabled={records.length === 0}
            onClick={() => {
              if (records.length === 0) { showToast('No records to export'); return; }
              printHistoryPDF(records, vehicles, invoices, companySettings, { from: filterFrom, to: filterTo });
            }}>🖨 PDF{anyFilter ? ` (${records.length})` : ''}</button>
        </div>
      </div>

      {/* Table */}
      <div className="tbl">
        <div className="tbl-hd" style={{ gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr .8fr' }}>
          <div className="th">Contract</div>
          <div className="th">Vehicle</div>
          <div className="th">Customer</div>
          <div className="th">Start</div>
          <div className="th">End</div>
          <div className="th">Total</div>
          <div className="th">Return Cond.</div>
          <div className="th"></div>
        </div>

        {records.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>No records found</div>
        )}

        {records.map(r => {
          const v    = getVehicle(r.vid);
          const cond = COND_MAP[r.condRet] || { cls: 's-av', label: 'Not recorded' };
          const inv  = getInvoice(r.id);
          return (
            <div key={r.id} className="tr" style={{ gridTemplateColumns: '1fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr .8fr' }}
              onClick={() => setSelected(r)}>
              <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{r.id}</div>
              <div>
                <div className="an">{v?.icon} {v?.name?.split('–')[0]?.trim() || r.vid}</div>
                <div className="ap">{v?.plate}</div>
              </div>
              <div style={{ fontSize: 11 }}>{r.customer}</div>
              <div>
                <div className="dv">{r.startLabel}{r.t1 ? ` · ${r.t1}` : ''}</div>
                {r.pickupLocName && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>📍 {r.pickupLocName}</div>}
              </div>
              <div>
                <div className="dv">{r.endLabel}{r.t2 ? ` · ${r.t2}` : ''}</div>
                {r.returnLocName && <div style={{ fontSize: 10, color: 'var(--tx3)' }}>📍 {r.returnLocName}</div>}
              </div>
              <div className="pv">€{r.agreedPrice}</div>
              <div><span className={`sp ${cond.cls}`}>{cond.label}</span></div>
              <div className="abtns" onClick={e => e.stopPropagation()}>
                <button className="ab ab-st" onClick={() => setSelected(r)}>View</button>
                {inv && (
                  <button className="ab" style={{ fontSize: 10 }}
                    onClick={() => openModal('view-invoice', inv.id)}>🧾</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {selected && (
        <HistoryDetailModal
          record={selected}
          vehicle={getVehicle(selected.vid)}
          invoice={getInvoice(selected.id)}
          invoiceList={getInvoices(selected.id)}
          onClose={() => setSelected(null)}
          onViewInvoice={(invId) => {
            const inv = invId
              ? invoices.find(i => i.id === invId)
              : getInvoice(selected.id);
            if (inv) { openModal('view-invoice', inv.id); setSelected(null); }
          }}
        />
      )}
    </div>
  );
}
